import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Box,
    Container,
    darken,
    Grid,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { setBookerObject } from '../../../../app/Slices/BookerSlice';
import {
    useCalculateFeesMutation,
    useGetAvailabilityQuery,
    useGetProfileDetailsQuery,
    useSubmitWaitListRequestMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import { getDefaultValues, waitListRequestSchema } from './Shared/schema';
import {
    getAvailableSpaces,
    getCheckinTimeArray,
    getCheckoutTimeArray,
    shouldValidateSameDayHireHours,
    subHours,
} from './Shared/reservationUtils';
import NotesSection from './FormSections/WaitlistRequestSections/NotesSection';
import WorkspaceType from './FormSections/WaitlistRequestSections/WorkspaceType';
import TimeSection from './FormSections/WaitlistRequestSections/TimeSection';
import RightSummary from './FormSections/WaitlistRequestSections/RightSummary';
import { GridColDef } from '@mui/x-data-grid';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ReservationPolicies from './Shared/ReservationPolicies';

export default function WaitListRequestContent(params: any) {
    const loggedInUser = UserRole();
    const experienceDetails = params?.reservationDetails;
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const theme = useTheme();

    const startDate = moment(location?.state?.dates[0].startDate).format(
        'YYYY-MM-DD',
    );
    const endDate = moment(location?.state?.dates[0].endDate).format(
        'YYYY-MM-DD',
    );

    const [dates, setDates] = useState(
        (
            location?.state?.reservationData?.dates || location?.state?.dates
        )?.map((date: any, index: number) => ({
            id: index,
            startDate: moment(date.startDate).format('YYYY-MM-DD'),
            endDate: moment(date.endDate).format('YYYY-MM-DD'),
        })),
    );

    const selectedSchema = waitListRequestSchema(experienceDetails);
    type Schema = z.infer<typeof selectedSchema>;

    const methods = useForm<Schema>({
        resolver: zodResolver(selectedSchema),
        mode: 'onChange',
        defaultValues: getDefaultValues(
            experienceDetails,
            startDate !== endDate,
            loggedInUser,
        ),
        shouldUnregister: false,
        shouldFocusError: true,
    });

    const {
        control,
        watch,
        handleSubmit,
        setValue,
        formState: { isSubmitting },
        reset,
    } = methods;

    useEffect(() => {
        if (location?.state?.reservationData) {
            const { bookingInfo } = location?.state.reservationData;
            const { overnightGuests, ...rest } = bookingInfo;
            reset({
                ...location?.state?.reservationData,
                bookingInfo:
                    bookingInfo.moduleType !== 'boat' && !overnightGuests
                        ? { ...rest, guests: bookingInfo?.adults }
                        : bookingInfo,
                policies: [],
            } as any);
        }
        if (location?.state?.times) {
            setValue(
                'bookingInfo.checkinTime',
                location.state.times.checkin as any,
            );
            setValue(
                'bookingInfo.checkoutTime',
                location.state.times.checkout as any,
            );
        }
        if (location?.state?.commercialSpaceType) {
            setValue(
                'bookingInfo.spaceType',
                location.state.commercialSpaceType as any,
            );
        }
    }, []);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [fees, setFees] = useState<any>();
    const [calculateFees] = useCalculateFeesMutation();
    const [requestWaitList] = useSubmitWaitListRequestMutation();

    const spaceType = watch('bookingInfo.spaceType');
    const isMeetingRoom = watch('bookingInfo.reqMeetingRoom');
    const checkinTime = watch('bookingInfo.checkinTime');
    const checkoutTime = watch('bookingInfo.checkoutTime');
    const visitType = experienceDetails?.visitType;
    const additionalNotes = watch('bookingInfo.notes');
    const checkinT =
        location?.state?.times?.checkinFrom || experienceDetails?.checkinTime;
    const checkoutT =
        location?.state?.times?.checkoutBefore ||
        experienceDetails?.checkoutTime;
    const timeValue =
        checkinTime && checkoutTime
            ? `${checkinTime} - ${checkoutTime}`
            : `${checkinT} - ${checkoutT}`;

    const { data: profileDetails } = useGetProfileDetailsQuery('/profile');

    const { data: availability, isLoading: isLoadingAvailability } =
        useGetAvailabilityQuery({
            url: `/modules/commercial/${experienceDetails.id}/get-availability`,
            body: { startDate, endDate, bookerId: loggedInUser?.sub },
        });

    const availableSpaces = getAvailableSpaces(availability, experienceDetails);

    const onClickGoBack = () => {
        navigate(
            `/viewExperiences/${experienceDetails?.type}/${experienceDetails?.id}`,
        );
    };

    const newTimeSelection = subHours(checkoutTime, checkinTime);

    useEffect(() => {
        let m = '';
        if (!checkinTime || !checkoutTime) return;
        const min = experienceDetails.minHireHours;
        const max = experienceDetails.maxHireHours;
        const add = moment(min, 'HH').format('HH:mm');
        const sub = moment(max, 'HH').format('HH:mm');
        const validateSameDayHours = shouldValidateSameDayHireHours(min, max);
        if (
            validateSameDayHours &&
            newTimeSelection < add &&
            startDate === endDate &&
            checkinTime < checkoutTime
        )
            m = `Your selection is below the allowed hire time for this experience ${min} hrs`;
        else if (
            validateSameDayHours &&
            newTimeSelection > sub &&
            startDate === endDate &&
            checkinTime <= checkoutTime &&
            sub !== '00:00'
        )
            m = `Your selection exceeds the allowed hire time for this experience ${min}-${max} hrs`;
        else if (startDate === endDate && checkinTime > checkoutTime)
            m = 'Checkin time cannot be greater than checkout time';
        if (m) setErrors((p) => ({ ...p, checkoutTime: m }));
        else setErrors(({ checkoutTime, ...r }) => r as any);
    }, [newTimeSelection, startDate, endDate, checkinTime, checkoutTime]);

    useEffect(() => {
        if (
            (experienceDetails?.type === 'commercial' && !spaceType) ||
            !startDate ||
            !visitType
        )
            return;
        calculateFees({
            bookerType: 'guest',
            module: { id: experienceDetails?.id },
            startDate,
            endDate,
            bookingInfo: {
                moduleType: experienceDetails?.type,
                visitType,
                checkinTime: checkinTime || '08:00',
                checkoutTime: checkoutTime || '17:00',
                adults: 1,
                children: 0,
                overnightGuests: 1,
                requiresCatering: false,
                cateringNotes: '',
                requiresBeverages: false,
                beverageNotes: '',
                requiresMassages: false,
                massageNotes: '',
                notes: additionalNotes,
                desks: spaceType === 'desk' ? 1 : 0,
                privateOffices: spaceType === 'privateOffice' ? 1 : 0,
                meetingRooms:
                    spaceType === 'meetingRoom' ||
                    (isMeetingRoom && spaceType !== 'entireVenue')
                        ? 1
                        : 0,
                entireVenue: spaceType === 'entireVenue',
                guests: 1,
                drivers: [],
                driverSameAsBooker: true,
            },
            policies: [],
            staffAttending: [],
            externalAttendees: [],
        }).then((data: any) => {
            if (!data?.error) setFees(data.data);
        });
    }, [
        checkinTime,
        checkoutTime,
        isMeetingRoom,
        spaceType,
        startDate,
        visitType,
        additionalNotes,
    ]);

    useEffect(() => {
        dispatch(setBookerObject(profileDetails));
    }, [profileDetails]);

    useEffect(() => {
        if (spaceType === 'entireVenue' || spaceType === 'meetingRoom')
            setValue('bookingInfo.reqMeetingRoom', false as any);
    }, [spaceType]);

    useEffect(() => {
        if (
            experienceDetails.type !== 'commercial' ||
            !availableSpaces ||
            !spaceType
        )
            return;
        const available = availableSpaces
            .filter((s) => !s.label.includes('unavailable'))
            .map((s) => s.value);
        if (available.includes(spaceType as string)) {
            navigate(
                `/experiences/reservationRequest/${experienceDetails?.type.toLowerCase()}/${experienceDetails?.name
                    .replace(/ /g, '')
                    .toLowerCase()}/${experienceDetails?.id}`,
                {
                    state: {
                        ...location?.state,
                        commercialSpaceType: spaceType,
                    },
                },
            );
        }
    }, [spaceType, availableSpaces]);

    const renderFees = () => {
        if (!fees) return false;

        return true;
    };
    const showFees =
        experienceDetails?.charges.length !== 0 &&
        !experienceDetails?.billingExcluded &&
        renderFees();

    const compareCheckinSettings = getCheckinTimeArray(
        startDate,
        endDate,
        checkoutT,
        checkinT,
        experienceDetails,
    );
    const compareCheckoutSettings = getCheckoutTimeArray(
        startDate,
        endDate,
        checkoutT,
        checkinT,
        experienceDetails,
        checkinTime as string,
    );

    const fullAddress = `${experienceDetails?.streetAddress} ${experienceDetails?.suburb} ${experienceDetails?.postcode} ${experienceDetails?.state} ${experienceDetails?.countryCode}`;

    const onSubmit = async (data: FieldValues) => {
        let additionalBookingInfo = {} as any;
        if (
            experienceDetails?.type === 'commercial' &&
            (data as any)?.bookingInfo
        ) {
            additionalBookingInfo = {
                checkinTime: experienceDetails?.checkinTime,
                checkoutTime: experienceDetails?.checkoutTime,
                desks: (data as any).bookingInfo.spaceType === 'desk' ? 1 : 0,
                privateOffices:
                    (data as any).bookingInfo.spaceType === 'privateOffice'
                        ? 1
                        : 0,
                meetingRooms:
                    (data as any).bookingInfo.spaceType === 'meetingRoom' ||
                    ((data as any).bookingInfo.reqMeetingRoom &&
                        (data as any).bookingInfo.spaceType !== 'entireVenue')
                        ? 1
                        : 0,
                entireVenue:
                    (data as any).bookingInfo.spaceType === 'entireVenue',
            };
        }
        const request = {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            checkinTime: (data as any).bookingInfo.checkinTime,
            checkoutTime: (data as any).bookingInfo.checkoutTime,
            module: {
                id: experienceDetails?.id,
                type: experienceDetails?.type,
            },
            notes: (data as any).bookingInfo.notes,
            ...additionalBookingInfo,
            ...(experienceDetails?.type === 'commercial'
                ? { commercialWaitListRequestInfo: additionalBookingInfo }
                : {}),
        } as any;
        const response = await requestWaitList(request).unwrap();
        navigate('/reservations?tab=4', {
            state: { page: 'waitlist', id: response.id },
        });
    };

    const label =
        startDate === endDate && dates?.length === 1 ? 'Date' : 'Dates';
    const value =
        startDate && endDate
            ? startDate === endDate
                ? moment(startDate).format('DD/MM/YYYY')
                : `${moment(startDate).format('DD/MM/YYYY')} - ${moment(
                      endDate,
                  ).format('DD/MM/YYYY')}`
            : '';

    const columns: GridColDef[] = [
        {
            field: 'startDate',
            headerName: 'Start Date',
            flex: 0.3,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <Typography variant='body1'>
                    {moment(params?.value).format('DD/MM/YYYY')}
                </Typography>
            ),
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            flex: 2,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <Typography variant='body1'>
                    {moment(params?.value).format('DD/MM/YYYY')}
                </Typography>
            ),
        },
        {
            field: 'action',
            headerName: '',
            flex: 0.1,
            sortable: false,
            width: 0,
            renderCell: (params) => {
                const onClick = () => {
                    const currentRowId = params?.id;
                    setDates((prev: any) =>
                        prev.filter((v: any) => v?.id !== currentRowId),
                    );
                };

                return (
                    <CustomIconButton
                        sx={{
                            backgroundColor: 'transparent',
                            width: '25px',
                            height: '25px',
                            '&:hover': {
                                cursor: 'pointer',
                                color:
                                    params?.row.isAvailable === true
                                        ? theme.palette.textAccent.main
                                        : theme.palette.red.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                        onClick={onClick}
                    >
                        <DeleteForeverIcon
                            fontSize='small'
                            sx={{ fontSize: '16px' }}
                        />
                    </CustomIconButton>
                );
            },
        },
    ];

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ py: { xs: 2, sm: 3 } }}>
                    <Container
                        maxWidth='lg'
                        sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}
                    >
                        <Paper
                            elevation={0}
                            square
                            sx={{
                                bgcolor: theme.palette.neutral.main,
                                p: { xs: 2, sm: 3, md: 3.5 },
                                borderRadius: { xs: 0, sm: 2 },
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    mb: { xs: 2, sm: 3 },
                                }}
                            >
                                <Typography
                                    variant='h1'
                                    sx={{
                                        textDecoration: 'none',
                                        textAlign: 'left',
                                        fontSize: {
                                            xs: '20px',
                                            sm: '22px',
                                            md: '24px',
                                        },
                                        lineHeight: 1.2,
                                        pr: { xs: 5, sm: 0 },
                                    }}
                                >
                                    {loggedInUser?.role.includes('owner')
                                        ? 'Waitlist'
                                        : 'Join Waitlist'}
                                </Typography>
                                <CustomButton
                                    onClick={onClickGoBack}
                                    variant='contained'
                                    color='inherit'
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        display: {
                                            xs: 'none',
                                            sm: 'inline-flex',
                                        },
                                        transform: {
                                            sm: 'translateX(calc(-100% - 16px))',
                                            md: 'translateX(calc(-100% - 24px))',
                                        },
                                        minWidth: 0,
                                        width: { sm: 36, md: 40 },
                                        height: { sm: 36, md: 40 },
                                        p: 0,
                                        borderRadius: '50%',
                                        backgroundColor:
                                            theme.palette.policy.fair,
                                        color: theme.palette.neutral.main,
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: darken(
                                                theme.palette.policy.fair,
                                                0.15,
                                            ),
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    <ArrowBackIcon fontSize='small' />
                                </CustomButton>
                            </Box>

                            <Grid
                                container
                                columnSpacing={{ xs: 0, sm: 2, md: 3 }}
                                rowSpacing={{ xs: 2, sm: 3 }}
                                alignItems='flex-start'
                            >
                                <Grid item xs={12} md={6} lg={7}>
                                    <Box
                                        sx={{
                                            bgcolor: theme.palette.neutral.main,
                                            p: { xs: 2, sm: 3 },
                                            borderRadius: 2,
                                            display: 'block',
                                        }}
                                    >
                                        <WorkspaceType
                                            experienceDetails={
                                                experienceDetails
                                            }
                                            availability={availability}
                                            control={control}
                                            spaceType={spaceType}
                                            isMeetingRoom={isMeetingRoom}
                                            setErrors={setErrors}
                                        />

                                        <TimeSection
                                            control={control}
                                            experienceType={
                                                experienceDetails?.type
                                            }
                                            checkinT={checkinT}
                                            checkoutT={checkoutT}
                                            compareCheckinSettings={
                                                compareCheckinSettings
                                            }
                                            compareCheckoutSettings={
                                                compareCheckoutSettings
                                            }
                                            checkoutError={errors.checkoutTime}
                                        />

                                        <NotesSection
                                            control={control}
                                            watch={watch}
                                        />
                                    </Box>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={5}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: theme.palette.neutral.main,
                                            p: { xs: 2, sm: 3 },
                                            borderRadius: 2,
                                            display: 'block',
                                        }}
                                    >
                                        <RightSummary
                                            experienceDetails={
                                                experienceDetails
                                            }
                                            fullAddress={fullAddress}
                                            label={label}
                                            value={value}
                                            fees={fees}
                                        />

                                        {!loggedInUser?.role?.includes(
                                            'owner',
                                        ) && <ReservationPolicies />}

                                        <Box sx={{ mt: 2 }}>
                                            <CustomButton
                                                type='submit'
                                                fullWidth
                                                variant='contained'
                                                color='primary'
                                                loading={isSubmitting}
                                                disabled={
                                                    isSubmitting ||
                                                    loggedInUser?.viewOnly
                                                }
                                            >
                                                Join Waitlist
                                            </CustomButton>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Container>
                </Box>
            </form>
        </FormProvider>
    );
}
